import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  ShieldCheck,
  Building2,
  FileText,
  Download,
  Calendar,
  Filter,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Clock,
} from 'lucide-react';
import AppLayout from '@/components/app/AppLayout';
import { useAuth } from '@/context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TAG_LABELS = {
  tax_filing: 'Tax Filing',
  legal_agreement: 'Legal Agreement',
  compliance: 'Compliance',
  annual_report: 'Annual Report',
  other: 'Other',
};

const TAG_STYLES = {
  tax_filing: 'bg-blue-500/15 text-blue-300',
  legal_agreement: 'bg-purple-500/15 text-purple-300',
  compliance: 'bg-amber-500/15 text-amber-300',
  annual_report: 'bg-emerald-500/15 text-emerald-300',
  other: 'bg-gray-500/15 text-gray-300',
};

const TagPill = ({ tag }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
      TAG_STYLES[tag] || TAG_STYLES.other
    }`}
    data-testid={`docvault-tag-${tag}`}
  >
    {TAG_LABELS[tag] || tag}
  </span>
);

const formatDate = (s) => {
  if (!s) return '-';
  try {
    return new Date(s).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '-';
  }
};

const formatBytes = (b) => {
  if (!b && b !== 0) return null;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const DocRow = ({ doc, onDownload, downloadingId }) => {
  const isDownloading = downloadingId === doc.id;
  const downloaded = doc.is_downloaded;
  const size = formatBytes(doc.file_size);

  return (
    <div
      className="bg-[#111827] border border-gray-800 rounded-lg p-4"
      data-testid={`docvault-row-${doc.id}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className="w-10 h-10 rounded-lg bg-[#059669]/10 flex items-center justify-center flex-shrink-0">
            <FileText className="w-5 h-5 text-[#059669]" />
          </div>
          <div className="min-w-0 flex-1">
            <h3
              className="text-white font-semibold truncate"
              data-testid={`docvault-title-${doc.id}`}
            >
              {doc.title}
            </h3>
            {doc.description && (
              <p className="text-gray-400 text-sm mt-1 line-clamp-2">{doc.description}</p>
            )}
            <div className="flex flex-wrap items-center gap-2 mt-2">
              {doc.tags?.length > 0 &&
                doc.tags.map((t) => <TagPill key={t} tag={t} />)}
              <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                <Calendar className="w-3 h-3" />
                {formatDate(doc.uploaded_at)}
              </span>
              {size && <span className="text-xs text-gray-500">· {size}</span>}
              <span className="text-xs text-gray-600">· {doc.file_name}</span>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-end gap-2 flex-shrink-0">
          {downloaded ? (
            <span
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-[#059669]/15 text-[#059669]"
              data-testid={`docvault-status-downloaded-${doc.id}`}
            >
              <CheckCircle2 className="w-3 h-3" />
              Downloaded
            </span>
          ) : (
            <span
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-500/15 text-amber-300"
              data-testid={`docvault-status-pending-${doc.id}`}
            >
              <Clock className="w-3 h-3" />
              Not downloaded
            </span>
          )}
          <button
            onClick={() => onDownload(doc)}
            disabled={isDownloading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#059669] text-gray-900 text-sm font-medium rounded-lg hover:opacity-90 disabled:opacity-60"
            data-testid={`docvault-download-btn-${doc.id}`}
          >
            {isDownloading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            {isDownloading ? 'Preparing…' : 'Download'}
          </button>
        </div>
      </div>
    </div>
  );
};

const DocVaultPage = () => {
  const { token } = useAuth();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState('all');
  const [downloadingId, setDownloadingId] = useState(null);

  const fetchData = async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/docvault`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGroups(res.data?.groups || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load DocVault');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleDownload = async (doc) => {
    setDownloadingId(doc.id);
    try {
      const res = await axios.post(
        `${API}/docvault/${doc.id}/download`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const url = res.data?.presigned_url;
      if (url) window.open(url, '_blank');
      // Optimistic local update + reconcile from server
      setGroups((prev) =>
        prev.map((g) => ({
          ...g,
          documents: g.documents.map((d) =>
            d.id === doc.id ? { ...d, is_downloaded: true } : d
          ),
        }))
      );
    } catch (err) {
      setError(err.response?.data?.detail || 'Download failed');
    } finally {
      setDownloadingId(null);
    }
  };

  // Build filtered/computed view
  const filteredGroups = useMemo(() => {
    let next = groups;
    if (companyFilter !== 'all') {
      next = next.filter((g) => g.company_id === companyFilter);
    }
    next = next
      .map((g) => ({
        ...g,
        documents:
          tagFilter === 'all'
            ? g.documents
            : g.documents.filter((d) => (d.tags || []).includes(tagFilter)),
      }))
      .filter((g) => g.documents.length > 0);
    return next;
  }, [groups, companyFilter, tagFilter]);

  const totalDocs = filteredGroups.reduce((acc, g) => acc + g.documents.length, 0);
  const allCompanies = groups.map((g) => ({
    id: g.company_id,
    name: g.company_name || 'Untitled',
  }));

  return (
    <AppLayout>
      <div className="p-6" data-testid="docvault-page">
        <div className="flex items-center gap-3 mb-2">
          <ShieldCheck className="w-7 h-7 text-[#059669]" />
          <h1 className="text-2xl font-bold text-white">DocVault</h1>
        </div>
        <p className="text-gray-400 mb-6">
          Documents uploaded by StartEase for your companies. Tax filings, legal agreements,
          compliance reports — all in one place.
        </p>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <span className="text-xs text-gray-500 uppercase tracking-wide">Filter</span>
          </div>
          <select
            value={companyFilter}
            onChange={(e) => setCompanyFilter(e.target.value)}
            className="px-3 py-1.5 bg-gray-800/60 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-[#059669]"
            data-testid="docvault-filter-company"
          >
            <option value="all">All companies</option>
            {allCompanies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <select
            value={tagFilter}
            onChange={(e) => setTagFilter(e.target.value)}
            className="px-3 py-1.5 bg-gray-800/60 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-[#059669]"
            data-testid="docvault-filter-tag"
          >
            <option value="all">All tags</option>
            {Object.entries(TAG_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm text-red-300 mb-4 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            {error}
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-[#059669] animate-spin" />
          </div>
        )}

        {!loading && totalDocs === 0 && (
          <div
            className="bg-gray-800/30 border border-gray-700/50 rounded-xl p-12 text-center"
            data-testid="docvault-empty"
          >
            <ShieldCheck className="w-10 h-10 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">
              No documents yet. Documents uploaded by StartEase will appear here.
            </p>
          </div>
        )}

        {!loading && totalDocs > 0 && (
          <div className="space-y-8" data-testid="docvault-groups">
            {filteredGroups.map((g) => (
              <div key={g.company_id}>
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="w-4 h-4 text-gray-400" />
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-400">
                    {g.company_name || 'Untitled'}
                  </h2>
                  <span className="text-xs text-gray-600">· {g.documents.length}</span>
                </div>
                <div className="space-y-3">
                  {g.documents.map((d) => (
                    <DocRow
                      key={d.id}
                      doc={d}
                      onDownload={handleDownload}
                      downloadingId={downloadingId}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default DocVaultPage;
